import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { client } from "../../../App";
import { getMaxValues } from "../../../helpers/helpers";
import { GET_DASHBOARD_CONSUME } from "../../../helpers/queries";
import Modal from "../../common/Modal/Modal";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);
Tooltip.positioners.followMouse = function (elements, eventPosition) {
  return eventPosition;
};

const BACKGROUND_COLORS = ["#00A694", "#D47D6E", "#9E88CC", "#019ED9"];

const getOrCreateTooltip = () => {
  let tooltipEl = document.getElementById("chartjs-tooltip");
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML =
      "<section style='position:relative;display:flex;flex-direction:column;gap:20px;width:100%;height:100%;'></section>";
    Object.assign(tooltipEl.style, {
      borderRadius: "8px",
      backgroundColor: "rgba(29,34,44,1)",
      color: "#fff",
      padding: "1rem",
      pointerEvents: "none",
      position: "absolute",
      opacity: 0,
      transition: "opacity .2s",
    });
    document.body.appendChild(tooltipEl);
  }
  return tooltipEl;
};

const externalTooltipHandler = (context) => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip();

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map((b) => b.lines);
    let innerHtml = "";

    titleLines.forEach((title) => {
      bodyLines.forEach((body, i) => {
        const lineStr = body[0] || "";
        const parts = lineStr.split(" ");
        let year = "",
          value = "";

        if (parts.length >= 2) {
          year = parts[0].replace(":", "");
          value = parts[1];
        } else {
          value = lineStr;
        }

        const color = tooltip.labelColors[i]?.backgroundColor || "#fff";

        innerHtml += `
          <div class='tooltip-year'>
            <h6 style='font-size:16px'>${title} <strong>${year}</strong></h6>
            <div style="display:flex;align-items:center;gap:10px">
              <span style="display:inline-block;width:15px;height:15px;border-radius:50%;background-color:${color}"></span>
              <h6 style='font-size:16px'><strong>${value} m<sup>3</sup></strong></h6>
            </div>
          </div>`;
      });
    });

    const section = tooltipEl.querySelector("section");
    if (section) section.innerHTML = innerHtml;
  }

  const position = chart.canvas.getBoundingClientRect();
  const tooltipWidth = tooltipEl.offsetWidth;
  const tooltipHeight = tooltipEl.offsetHeight;

  let left =
    position.left + window.pageXOffset + tooltip.caretX - tooltipWidth / 2;

  let top =
    position.top + window.pageYOffset + tooltip.caretY - tooltipHeight - 5;

  if (top < window.pageYOffset + 104) {
    top = position.top + window.pageYOffset + tooltip.caretY + 15;
  }

  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = left + "px";
  tooltipEl.style.top = top + "px";
  tooltipEl.style.transition = "top 0.1s ease, left 0.1s ease, opacity 0.2s"; // Animazione fluida
};

const ConsumeChart = ({ contract }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const chartRef = useRef(null); // Nuovo approccio ref

  const monthShortLabels = useMemo(
    () => [
      t("Gen"),
      t("Feb"),
      t("Mar"),
      t("Apr"),
      t("Mag"),
      t("Giu"),
      t("Lug"),
      t("Ago"),
      t("Set"),
      t("Ott"),
      t("Nov"),
      t("Dic"),
    ],
    [t]
  );

  const monthFullLabels = useMemo(
    () => [
      t("months.full.january"),
      t("months.full.february"),
      t("months.full.march"),
      t("months.full.april"),
      t("months.full.may"),
      t("months.full.june"),
      t("months.full.july"),
      t("months.full.august"),
      t("months.full.september"),
      t("months.full.october"),
      t("months.full.november"),
      t("months.full.december"),
    ],
    [t]
  );

  const [consumeData, setConsumeData] = useState({
    labels: monthShortLabels,
    datasets: [],
  });
  const [flowError, setFlowError] = useState(false);


  const processChartData = useCallback(
    (data) => {
      if (!data || !data.length) return;

      const uniqueYears = [...new Set(data.map((d) => Number(d.year)))];
      const lastYears = getMaxValues(uniqueYears, 2);

      const newDatasets = [];

      lastYears.forEach((year, index) => {
        const yearData = data.filter((d) => Number(d.year) === Number(year));

        if (yearData.length > 0) {
          const consumptionPerYear = Array(12)
            .fill(0)
            .map((_, monthIndex) => {
              const found = yearData.find(
                (d) => Number(d.month) - 1 === monthIndex
              );
              return found ? found.consumption : 0;
            });

          const colorIndex =
            BACKGROUND_COLORS.length - (lastYears.length - index);

          newDatasets.push({
            label: String(year),
            id: index,
            data: consumptionPerYear,
            backgroundColor:
              BACKGROUND_COLORS[colorIndex] || BACKGROUND_COLORS[0],
            barPercentage: 0.6,
            categoryPercentage: 0.8,
          });
        }
      });

      setConsumeData({
        labels: monthShortLabels,
        datasets: newDatasets,
      });
    },
    [monthShortLabels]
  );

  useEffect(() => {
    if (!contract?.id) return;

    const fetchData = async () => {
      try {
        const { data } = await client().query({
          query: GET_DASHBOARD_CONSUME,
          variables: { contractId: contract.id },
        });
        const consumptionData = data?.getDashboardConsumptions?.consumptions;
        processChartData(consumptionData);
      } catch (e) {
        setFlowError(true);
      }
    };

    fetchData();
  }, [contract, processChartData]);

  useEffect(() => {
    return () => {
      const tooltipEl = document.getElementById("chartjs-tooltip");
      if (tooltipEl) {
        tooltipEl.remove();
      }
    };
  }, []);

  const options = useMemo(
    () => ({
      responsive: true,
      scales: {
        y: { display: true, beginAtZero: true },
        x: { display: true, grid: { display: true } },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          position: "followMouse",
          mode: "index",
          intersect: false,
          external: (context) => externalTooltipHandler(context),
        },
      },
      interaction: { mode: "index", intersect: false },
    }),
    []
  );

  const handleLegendClick = (datasetIndex, color) => {
    const chart = chartRef.current;
    if (!chart) return;

    const meta = chart.getDatasetMeta(datasetIndex);
    const element = document.getElementById("legend-label-" + datasetIndex);

    if (element) {
      const currentBg = element.style.backgroundColor;
      element.style.backgroundColor =
        currentBg === "white" || currentBg === "rgb(255, 255, 255)"
          ? color
          : "white";
    }

    meta.hidden =
      meta.hidden === null
        ? !chart.data.datasets[datasetIndex].hidden
        : !meta.hidden;
    chart.update();
  };

  const dataLabels = consumeData.datasets.map((d) => d.label);
  const colors = consumeData.datasets.map((d) => d.backgroundColor);

  return (
    <div className="consume-chart-container">
      <p className="headline-fs5">{t("consumptionGraph.title")}</p>

      <table
        className="sr-only"
        aria-label={t("consumptionGraph.accessibleTable")}
      >
        <caption>{t("consumptionGraph.title")}</caption>
        <thead>
          <tr>
            <th>{t("common.month")}</th>
            {consumeData.datasets.map((ds) => (
              <th key={ds.label}>{ds.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {monthFullLabels.map((month, i) => (
            <tr key={month}>
              <td>{month}</td>
              {consumeData.datasets.map((ds) => (
                <td key={ds.label}>{ds.data[i]} m³</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <ul
        className="chart-legend"
        role="list"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          margin: "0 0 30px",
          flexWrap: "wrap",
          justifyContent: "end",
        }}
      >
        {dataLabels.map((label, i) => {
          const isHidden = chartRef.current?.getDatasetMeta(i)?.hidden || false;

          return (
            <li key={i} role="none">
              <div
                role="checkbox"
                tabIndex={0}
                aria-checked={!isHidden}
                aria-label={`${t("consumptionGraph.toggleYear")} ${label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLegendClick(i, colors[i]);
                }}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    handleLegendClick(i, colors[i]);
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  background: "rgba(29, 34, 44, 1)",
                  padding: "8px 16px 8px 8px",
                  color: "rgba(255, 255, 255, 1)",
                  borderRadius: "28px",
                  fontSize: "14px",
                  outline: "none",
                }}
              >
                <span
                  id={"legend-label-" + i}
                  style={{
                    display: "inline-block",
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: colors[i],
                    border: "1px solid " + colors[i], 
                  }}
                ></span>
                {label}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="chart-scroll-wrapper">
        <div className="chart-container">
          <Bar ref={chartRef} data={consumeData} options={options} />
        </div>
      </div>

      <p className="paragraph-large-fs7">{t("consumptionGraph.subtitle")}</p>

      <Modal
        show={flowError}
        onClose={() => setFlowError(false)}
        description={t("consumptionGraph.errorMessage")}
        type="button"
        onFirstClick={() => setFlowError(false)}
        linkTitle={t("common.hoCapito")}
      />
    </div>
  );
};

export default ConsumeChart;